import { ArrowBack, ArrowForward } from 'assets/icons';
import Button from 'components/common/Button';
import { Formik, type FormikHelpers } from 'formik';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import SaveIcon from 'assets/icons/save.svg?react';
import s from '../../calculators.module.scss';
import BuyingHouseEstimationForm from './BuyingHouseEstimationForm';
import validationSchema from '../yupSchemas/buyingHouseSecondStepSchema';
import type { IBuyingHouseFormValues, IBuyingHouseStepProps } from '../types';

interface IButtonsProps {
  isEditMode?: boolean;
  onBack: IBuyingHouseStepProps['onBack'];
  values: IBuyingHouseFormValues;
  previousStep?: () => void;
  isSubmitting: boolean;
}

const Buttons = ({ isEditMode, onBack, values, previousStep, isSubmitting }: IButtonsProps) => {
  const { formatMessage: t } = useIntl();

  return (
    <div className={s.buttonsWrapper}>
      {!isEditMode ? (
        <Button
          disabled={isSubmitting}
          onClick={() => onBack(values, previousStep)}
          startIcon={<ArrowBack />}
        >
          {t({ id: 'app.back' })}
        </Button>
      ) : null}
      <Button
        type="submit"
        loading={isSubmitting}
        disabled={isSubmitting}
        endIcon={isEditMode ? <SaveIcon /> : <ArrowForward />}
        className="ml-auto"
      >
        {t({ id: `app.${isEditMode ? 'save' : 'submit_form_next'}` })}
      </Button>
    </div>
  );
};

const BuyingHouseSecondStep = ({
  currentStep,
  formValuesRef,
  isEditMode,
  lastStep,
  onSubmit,
  onSubmitStep,
  nextStep,
  previousStep,
  hasAdditionalFeatures,
  onBack,
}: IBuyingHouseStepProps) => {
  const onSubmitFormHandler = useCallback(
    async (
      values: IBuyingHouseFormValues,
      formikActions: FormikHelpers<IBuyingHouseFormValues>,
    ) => {
      const step = !isEditMode && values.additional_features ? currentStep : null;
      onSubmitStep(values);
      await onSubmit({
        values,
        formikActions,
        currentStep: step,
        isEditMode: !values.additional_features,
        lastStep,
        nextStep,
      });
    },
    [currentStep, isEditMode, lastStep, nextStep, onSubmit, onSubmitStep],
  );

  return (
    <Formik<IBuyingHouseFormValues>
      initialValues={formValuesRef?.current}
      onSubmit={onSubmitFormHandler}
      validationSchema={validationSchema}
    >
      {(props) => (
        <BuyingHouseEstimationForm
          {...{
            hasAdditionalFeatures,
            buttons: <Buttons {...{ isEditMode, onBack, previousStep, ...props }} />,
            ...props,
          }}
        />
      )}
    </Formik>
  );
};

export default BuyingHouseSecondStep;
